const KEY = 'et.returnPath';

/**
 * The in-app route a user was headed to when sign-in interrupted them, kept across the Microsoft
 * redirect (BUG-054, F5-a FR-3 / AC-5 / AC-8).
 *
 * The gate preserves the attempted route in router `location.state`, which is enough for the stub
 * sign-in: it never leaves the page. The real sign-in is a full-page navigation to Microsoft and back,
 * which empties router state, and the backend returns the browser to the SPA root. So the login page
 * stores the route here just before it leaves (`startMicrosoftSignIn`), and the authenticated gate
 * reads it once the session is confirmed, clears it, and goes there.
 *
 * `sessionStorage`, not `localStorage`: the route belongs to this tab's sign-in, and must not send a
 * second tab somewhere. Every access is wrapped — storage throws when it is disabled or in some
 * private modes, and a throw here must fall back to the default landing, never break the sign-in.
 */

/** True for a C0 control character or DEL — a browser strips some of these from a URL. */
function hasControlCharacter(value: string): boolean {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 0x1f || code === 0x7f) {
            return true;
        }
    }
    return false;
}

/**
 * `value` when it is a same-origin in-app path (`/accounts?tab=2#notes`), else `undefined`.
 *
 * Only a string that starts with exactly one `/` passes. `//host` and `/\host` are rejected because
 * a browser reads both as another host; any backslash or control character is rejected because a
 * browser rewrites them (a tab inside `/<TAB>/host` is dropped, leaving `//host`); a scheme
 * (`https:`, `javascript:`) fails the leading `/`. The result is then resolved against this origin
 * and must stay on it, and must not be the login page itself (`/login`, in any case, with or without
 * a trailing slash, search or hash): returning there after sign-in would only land the user back on
 * the page they just left. One rule for every place a return route is read — router state and
 * storage.
 */
export function safeReturnPath(value: unknown): string | undefined {
    if (typeof value !== 'string' || !value.startsWith('/')) {
        return undefined;
    }
    if (value.startsWith('//') || value.includes('\\') || hasControlCharacter(value)) {
        return undefined;
    }
    try {
        const origin = window.location.origin;
        const resolved = new URL(value, origin);
        if (resolved.origin !== origin) {
            return undefined;
        }
        if (resolved.pathname.toLowerCase().replace(/\/+$/, '') === '/login') {
            return undefined;
        }
    } catch {
        return undefined;
    }
    // The raw value is returned on purpose, never `resolved`'s normalised path: normalising can
    // produce `//host` (`/..//evil` resolves to the pathname `//evil`), which a browser reads as
    // another host. The raw value passed every check above as it stands.
    return value;
}

/**
 * Keep `path` for after the Microsoft redirect. With no safe path to keep, a route kept by an earlier
 * attempt in this tab stays: a sign-in that failed (`?login=failed`) comes back to the login page with
 * no router state, and the retry is still on its way to the same route. A kept route is cleared once
 * a session is confirmed, so it never outlives the sign-in it belongs to.
 *
 * The trade-off of keeping it, accepted on purpose:
 * - a retry after `?login=failed` still returns to the kept route, even if the user has since
 *   decided they no longer want to go there;
 * - until a session is confirmed the route lives for the whole tab, and a duplicated tab copies
 *   `sessionStorage`, so the copy carries it too;
 * - what that can cost is bounded by the route itself: following it only opens an in-app page, and
 *   that page's own permission check still decides what the user sees there.
 */
export function storeReturnPath(path: string | undefined): void {
    const safe = safeReturnPath(path);
    if (safe === undefined) {
        return;
    }
    try {
        sessionStorage.setItem(KEY, safe);
    } catch {
        // Storage unavailable: the user lands on the default page after sign-in, as before.
    }
}

/** The kept route, if there is one and it is still a safe path. Reads only; `clearReturnPath` removes it. */
export function peekReturnPath(): string | undefined {
    try {
        return safeReturnPath(sessionStorage.getItem(KEY));
    } catch {
        return undefined;
    }
}

/** Forget the kept route — once it has been followed, or found not to be safe. */
export function clearReturnPath(): void {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        // Storage unavailable: nothing was kept.
    }
}
